import * as L from 'leaflet';
import {CanvasInitialView} from '@shared/models/structure';
import {CANVAS_CLUSTER_ICON, DEFAULT_TILE_COLOR, aggregateAlarmBadgeStage, AlarmBadgeStage} from './canvas-tile.constants';
import {CanvasMapSetup, ControlButtonOptions} from './canvas-map-controls.models';

// Backend sends this zero-valued object instead of omitting InitialView when unset.
export const UNSET_INITIAL_VIEW: CanvasInitialView = { CenterX: 0, CenterY: 0, Zoom: 0 };

// Whole-level zoom snap matches markercluster's integer-only zoom bucketing exactly
// (any fractional rest position, including half-levels, causes a real clustering/render
// mismatch, not just an occasional one). zoomAnimation eases the whole-level jump instead.
export const CANVAS_MAP_CONFIG: any = {
  crs: L.CRS.Simple,
  minZoom: -5,
  maxZoom: 2,
  zoomSnap: 1,
  zoomDelta: 1,
  attributionControl: false,
  zoomControl: false,
  scrollWheelZoom: true,
  doubleClickZoom: true,
  dragging: true,
  preferCanvas: true,
  gestureHandling: true, // Require Ctrl+scroll to zoom (from leaflet-gesture-handling)
  wheelPxPerZoomLevel: 120, // Higher = slower, more deliberate zoom (default is 60)
  wheelDebounceTime: 20,
  zoomAnimation: true,
};

export function hasSavedInitialView(view: CanvasInitialView | undefined): boolean {
  return !!view && (view.CenterX !== UNSET_INITIAL_VIEW.CenterX
    || view.CenterY !== UNSET_INITIAL_VIEW.CenterY
    || view.Zoom !== UNSET_INITIAL_VIEW.Zoom);
}

function buildConicGradient(colors: string[]): string {
  if (colors.length === 0) return DEFAULT_TILE_COLOR;
  if (colors.length === 1) return colors[0];

  const segmentDeg = 360 / colors.length;
  const parts: string[] = [];
  let deg = 0;
  colors.forEach(color => {
    parts.push(`${color} ${deg}deg ${deg + segmentDeg}deg`);
    deg += segmentDeg;
  });
  return `conic-gradient(${parts.join(', ')})`;
}

export function createCanvasClusterIcon(members: L.Marker[]): L.DivIcon {
  const { OUTER_SIZE_LARGE, OUTER_SIZE_SMALL, COUNT_THRESHOLD, RING_THICKNESS, FONT_SIZE_LARGE, FONT_SIZE_SMALL } = CANVAS_CLUSTER_ICON;
  const colors: string[] = members.flatMap((m: any) => m._tileColors ?? [DEFAULT_TILE_COLOR]);
  const count = colors.length;

  const size = count >= COUNT_THRESHOLD ? OUTER_SIZE_LARGE : OUTER_SIZE_SMALL;
  const innerSize = size - RING_THICKNESS;
  const fontSize = count >= COUNT_THRESHOLD ? FONT_SIZE_LARGE : FONT_SIZE_SMALL;
  const gradient = buildConicGradient(colors);

  const alarmBadgeStages: AlarmBadgeStage[] = members.map((m: any) => m._tileAlarmBadge ?? null);
  const alarmBadge = aggregateAlarmBadgeStage(alarmBadgeStages);
  const alarmBadgeHtml = alarmBadge
    ? `<div class="canvas-cluster-alarm-badge ${alarmBadge}"></div>`
    : '';

  const html = `
    <div class="canvas-cluster-outer" style="width:${size}px;height:${size}px;background:${gradient}">
      <div class="canvas-cluster-inner" style="width:${innerSize}px;height:${innerSize}px;font-size:${fontSize}px">
        ${count}
      </div>
      ${alarmBadgeHtml}
    </div>`;

  return L.divIcon({ html, className: '', iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
}

interface CanvasClusterEntry {
  badge: L.Marker;
  members: L.Marker[];
}

interface CanvasCluster {
  members: L.Marker[];
  sumX: number;
  sumY: number;
  cx: number;
  cy: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  cellKey: string;
}

/**
 * Groups canvas mini-tile markers into badges. Unlike leaflet.markercluster (which merges
 * markers transitively with no cap on the resulting cluster's span, and renders the badge
 * at whichever member happened to be added first), each cluster here has both a proximity
 * check (only merges markers actually near its current centroid) and a hard cap on its
 * total bounding-box size, and the badge always renders at the true centroid of its members.
 */
export class CanvasClusterLayer {
  private readonly singlesLayer = L.layerGroup();
  private readonly badgesLayer = L.layerGroup();
  private readonly onZoomEnd = () => this.recompute();
  private allMarkers: L.Marker[] = [];
  private enabled = true;
  private memberEntry = new Map<L.Marker, CanvasClusterEntry>();

  constructor(
    private readonly map: L.Map,
    private readonly cellSize: number,
    private readonly disableClusteringAtZoom: number,
    private readonly onClusterClick: (members: L.Marker[]) => void,
  ) {}

  addTo(map: L.Map): this {
    this.singlesLayer.addTo(map);
    this.badgesLayer.addTo(map);
    map.on('zoomend', this.onZoomEnd);
    return this;
  }

  remove(): void {
    this.map.off('zoomend', this.onZoomEnd);
    this.singlesLayer.remove();
    this.badgesLayer.remove();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.recompute();
  }

  setMarkers(markers: Iterable<L.Marker>): void {
    this.allMarkers = [...markers];
    this.recompute();
  }

  addMarker(marker: L.Marker): void {
    this.allMarkers.push(marker);
    this.recompute();
  }

  removeMarker(marker: L.Marker): void {
    this.allMarkers = this.allMarkers.filter(m => m !== marker);
    this.singlesLayer.removeLayer(marker);
    this.recompute();
  }

  // Re-renders a marker's badge (color/alarm changed) without recomputing grouping.
  refreshMarker(marker: L.Marker): void {
    const entry = this.memberEntry.get(marker);
    entry?.badge.setIcon(createCanvasClusterIcon(entry.members));
  }

  private recompute(): void {
    this.singlesLayer.clearLayers();
    this.badgesLayer.clearLayers();
    this.memberEntry.clear();

    const zoom = this.map.getZoom();
    if (!this.enabled || zoom >= this.disableClusteringAtZoom) {
      this.allMarkers.forEach(m => this.singlesLayer.addLayer(m));
      return;
    }

    const clusters = this.buildClusters(zoom);

    for (const cluster of clusters) {
      if (cluster.members.length === 1) {
        this.singlesLayer.addLayer(cluster.members[0]);
        continue;
      }

      const center = this.map.unproject(L.point(cluster.cx, cluster.cy), zoom);
      const badge = L.marker(center, { icon: createCanvasClusterIcon(cluster.members), interactive: true });
      badge.on('click', () => this.onClusterClick(cluster.members));
      this.badgesLayer.addLayer(badge);

      const entry: CanvasClusterEntry = { badge, members: cluster.members };
      cluster.members.forEach(m => this.memberEntry.set(m, entry));
    }
  }

  // Groups markers via nearest-cluster-within-radius (so real neighbors always merge,
  // regardless of where a fixed grid's cell boundaries happen to fall), but rejects a
  // merge that would grow the cluster's bounding box past maxDiameter, capping how far
  // a chain of near-neighbors can span in total.
  private buildClusters(zoom: number): CanvasCluster[] {
    const maxProximity = this.cellSize;
    const maxDiameter = this.cellSize * 2;
    const cellOf = (x: number, y: number) => `${Math.floor(x / this.cellSize)}:${Math.floor(y / this.cellSize)}`;
    const byCell = new Map<string, CanvasCluster[]>();

    const registerCell = (cluster: CanvasCluster) => {
      const key = cellOf(cluster.cx, cluster.cy);
      if (key === cluster.cellKey) return;
      if (cluster.cellKey) {
        const prev = byCell.get(cluster.cellKey);
        const i = prev?.indexOf(cluster) ?? -1;
        if (prev && i !== -1) prev.splice(i, 1);
      }
      cluster.cellKey = key;
      const list = byCell.get(key);
      if (list) list.push(cluster); else byCell.set(key, [cluster]);
    };

    const findMergeCandidate = (x: number, y: number): CanvasCluster | undefined => {
      const cx = Math.floor(x / this.cellSize);
      const cy = Math.floor(y / this.cellSize);
      let best: CanvasCluster | undefined;
      let bestDist = Infinity;
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          for (const cluster of byCell.get(`${cx + dx}:${cy + dy}`) ?? []) {
            const dist = Math.hypot(cluster.cx - x, cluster.cy - y);
            if (dist > maxProximity || dist >= bestDist) continue;
            const width = Math.max(cluster.maxX, x) - Math.min(cluster.minX, x);
            const height = Math.max(cluster.maxY, y) - Math.min(cluster.minY, y);
            if (width > maxDiameter || height > maxDiameter) continue;
            best = cluster;
            bestDist = dist;
          }
        }
      }
      return best;
    };

    const clusters: CanvasCluster[] = [];
    for (const marker of this.allMarkers) {
      const p = this.map.project(marker.getLatLng(), zoom);
      const candidate = findMergeCandidate(p.x, p.y);

      if (candidate) {
        candidate.members.push(marker);
        candidate.sumX += p.x;
        candidate.sumY += p.y;
        candidate.cx = candidate.sumX / candidate.members.length;
        candidate.cy = candidate.sumY / candidate.members.length;
        candidate.minX = Math.min(candidate.minX, p.x);
        candidate.maxX = Math.max(candidate.maxX, p.x);
        candidate.minY = Math.min(candidate.minY, p.y);
        candidate.maxY = Math.max(candidate.maxY, p.y);
        registerCell(candidate);
        continue;
      }

      const cluster: CanvasCluster = {
        members: [marker], sumX: p.x, sumY: p.y, cx: p.x, cy: p.y,
        minX: p.x, maxX: p.x, minY: p.y, maxY: p.y, cellKey: '',
      };
      registerCell(cluster);
      clusters.push(cluster);
    }

    return clusters;
  }
}

export interface CanvasMapControlsOptions {
  /** Called when the fit-to-view button is clicked. */
  onFitBounds: () => void;
  /** When provided, a cluster-toggle button is rendered. */
  clusterToggle?: {
    enabled: boolean;
    onChange: (enabled: boolean) => void;
  };
  snapToggle?: {
    enabled: boolean;
    onChange: (enabled: boolean) => void;
  };
}

function createControlButton({title, icon, onClick, parent, className = 'cmc-btn'}: ControlButtonOptions): HTMLButtonElement {
  const button = L.DomUtil.create('button', className, parent) as HTMLButtonElement;
  button.type = 'button';
  button.title = title;
  button.innerHTML = `<i class="${icon}"></i>`;
  button.addEventListener('click', onClick);
  return button;
}

function createToggleButton(
  parent: HTMLElement,
  toggle: {enabled: boolean; onChange: (enabled: boolean) => void},
  title: string,
  icon: string,
): void {
  let enabled = toggle.enabled;
  const button = createControlButton({
    parent, title, icon,
    onClick: () => {
      enabled = !enabled;
      button.classList.toggle('active', enabled);
      toggle.onChange(enabled);
    },
  });
  button.classList.toggle('active', enabled);
}

export function addCanvasMapControls(map: L.Map, options: CanvasMapControlsOptions): L.Control {
  const CanvasControl = L.Control.extend({
    onAdd(): HTMLElement {
      const container = L.DomUtil.create('div', 'canvas-map-controls');
      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);

      const zoomGroup = L.DomUtil.create('div', 'cmc-group', container);
      const groupButton = {parent: zoomGroup, className: 'cmc-btn cmc-group__btn'};
      createControlButton({...groupButton, title: 'Zoom in', icon: 'fa-regular fa-plus', onClick: () => map.zoomIn()});
      L.DomUtil.create('div', 'cmc-group__divider', zoomGroup);
      createControlButton({...groupButton, title: 'Zoom out', icon: 'fa-regular fa-minus', onClick: () => map.zoomOut()});

      createControlButton({parent: container, title: 'Fit to view', icon: 'fa-regular fa-compass', onClick: () => options.onFitBounds()});

      if (options.clusterToggle) {
        createToggleButton(container, options.clusterToggle, 'Toggle tile clustering', 'fa-regular fa-layer-group');
      }
      if (options.snapToggle) {
        createToggleButton(container, options.snapToggle, 'Toggle snap to grid', 'fa-solid fa-grid');
      }

      return container;
    },
  });

  const control = new CanvasControl({ position: 'topleft' });
  control.addTo(map);
  return control;
}

export function createCanvasMap(
  elementId: string,
  imageUrl: string,
  size: {width: number; height: number},
  initialView: CanvasInitialView | undefined,
  fitOptions: L.FitBoundsOptions,
  onImageLoad: () => void,
): CanvasMapSetup {
  const bounds = L.latLngBounds([[0, 0], [size.height, size.width]]);
  const fitToImage = () => map.fitBounds(bounds, fitOptions);
  const map = L.map(elementId, CANVAS_MAP_CONFIG);
  L.imageOverlay(imageUrl, bounds).on('load', onImageLoad).addTo(map);

  if (hasSavedInitialView(initialView)) {
    map.setView([initialView!.CenterY * size.height, initialView!.CenterX * size.width], initialView!.Zoom, {animate: false});
  } else {
    fitToImage();
  }
  return {map, fitToImage};
}

// Workaround: disable gestureHandling before map.remove() or its removeHooks() re-registers
// a Draggable listener on the already-detached _mapPane, leaving an orphaned event handler.
export function destroyCanvasMap(map: L.Map): void {
  const gestureHandling = (map as any).gestureHandling;
  if (gestureHandling?._enabled) {
    gestureHandling.disable();
  }

  const scrollZoom = (map as any).scrollWheelZoom;
  if (scrollZoom?._timer) {
    clearTimeout(scrollZoom._timer);
    scrollZoom._timer = null;
  }

  map.dragging.disable();
  map.scrollWheelZoom.disable();
  map.doubleClickZoom.disable();
  map.boxZoom.disable();
  map.keyboard.disable();
  map.remove();
}
